import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';

const navItems = [
  { label: 'Home', id: 'hero', index: '01' },
  { label: 'About', id: 'about', index: '02' },
  { label: 'Experience', id: 'experience', index: '03' },
  { label: 'Education', id: 'education', index: '04' },
  { label: 'Credentials', id: 'certification', index: '05' },
  { label: 'Projects', id: 'projects', index: '06' },
  { label: 'Skills', id: 'skills', index: '07' },
  { label: 'Archive', id: 'gallery', index: '08' },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);

      const sections = navItems.map((item) => ({
        id: item.id,
        element: document.getElementById(item.id),
      }));

      const currentSection = sections.find((section) => {
        if (section.element) {
          const rect = section.element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });

      if (currentSection) {
        setActiveSection(currentSection.id);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleNavClick = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      window.scrollTo({
        top: element.offsetTop - offset,
        behavior: 'smooth',
      });
    }
    setIsOpen(false);
  };

  return (
    <>
      <nav
        aria-label="Primary"
        className={`fixed top-0 left-0 right-0 z-40 border-b border-white/10 backdrop-blur-lg transition-colors duration-200 ${
          scrolled ? 'bg-black/60' : 'bg-black/40'
        }`}
      >
        <div className="container-max section-padding">
          <div className="flex h-16 items-center justify-between md:h-20">
            <button
              type="button"
              onClick={() => handleNavClick('hero')}
              className="flex items-center gap-3 text-left"
              aria-label="Go to top"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-r from-btc-burn to-btc-orange font-heading text-sm font-bold text-btc-void shadow-glow-btn">
                W
              </span>
              <span className="font-heading text-sm font-semibold tracking-tight">WGP</span>
            </button>

            <div className="hidden items-center gap-5 lg:flex">
              {navItems.map((item) => {
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleNavClick(item.id)}
                    data-active={isActive}
                    className="btc-nav-link"
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className="icon-btn lg:hidden"
              aria-expanded={isOpen}
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
            >
              {isOpen ? <X size={20} strokeWidth={2} /> : <Menu size={20} strokeWidth={2} />}
            </button>
          </div>
        </div>
      </nav>

      {isOpen && (
        <div className="fixed inset-0 z-30 bg-btc-void pt-20 lg:hidden">
          <div className="flex h-full flex-col overflow-auto px-6 pb-8">
            {navItems.map((item) => {
              const isActive = activeSection === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleNavClick(item.id)}
                  className={`flex min-h-[56px] items-baseline justify-between border-b border-white/10 text-left ${
                    isActive ? 'text-btc-orange' : 'text-white'
                  }`}
                >
                  <span className="font-heading text-3xl font-semibold tracking-tight">
                    {item.label}
                  </span>
                  <span className="font-mono text-xs tracking-widest text-btc-muted">
                    {item.index}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
